import React, { useContext, useEffect, useReducer, useState } from 'react';
import { useNavigate } from 'react-router';
import { createPortal } from 'react-dom';
import { usePageHeader } from '../../user';
import ExistingProjectCard from '../components/existing-project-cards';
import NewProjectModal from './automator-projects/add-edit-automator';
import { existingProjectsText, newProjectText, noProjectText, pageTitle } from '../data/constants';
import { getWorkflowProjectsList } from '../services';
import { AutomatorAction, AutomatorReducer, AutomatorState } from '../automator.reducer';
import { DisableWorkflowContext, WorkFlowContext } from '../../../../utils/context';
import { PermissionTypes } from '../../../../utils/interface-types';
import { verifyAccess } from '../../../../services';
import makeToast from '../../../../components/toaster';
import ErrorFallback from '../../../../components/error-fallback';
import { PlusIcon } from '../../../../assets/images';
import { Loader, Pagination, SearchBar } from '../../../../components';
import Logout from '../../../../auth/protected/session-expire-modal';

const initialState: AutomatorState = {
  appFunctions: { search: '', limit: 9, offset: 0 },
  appsData: { total_data: 0, data: [] },
  loading: false,
  newProjectModal: false,
  dataUpdated: false,
  updatePageAsPerSearch: { my_projects: false },
};

function Automator() {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer<React.Reducer<AutomatorState, AutomatorAction>>(
    AutomatorReducer,
    initialState,
  );
  const { setDisableMode } = useContext(DisableWorkflowContext);
  const [showError, setShowError] = useState<boolean>(false);
  const { workFlowData, setWorkFlowData } = useContext(WorkFlowContext);
  const [logout, setLogout] = useState<boolean>(false);

  const { appFunctions, appsData, loading, newProjectModal, dataUpdated, updatePageAsPerSearch } =
    state;

  const permissionsJson = localStorage.getItem('permissions');
  let permittedAccess: PermissionTypes[] = [];
  if (permissionsJson !== null) {
    const parsedPermissions: { data: Array<string>; module: string }[] =
      JSON.parse(permissionsJson);
    permittedAccess = parsedPermissions.filter(
      (permission) => permission.module === 'automator' && permission.data,
    );
  }
  useEffect(() => {
    localStorage.removeItem('workFlowData');
    localStorage.removeItem('disableMode');
  }, []);

  useEffect(() => {
    const hasAccess = verifyAccess('automator', '/automator');
    if (!hasAccess) {
      navigate('/unauthorized');
    }
  }, []);

  useEffect(() => {
    dispatch({ type: 'SET_LOADING', payload: true });
    localStorage?.setItem('triggerResponse', '[]');
    getWorkflowProjectsList(appFunctions)
      ?.then((res) => {
        if (res?.status === 200) {
          dispatch({
            type: 'SET_APPS_DATA',
            payload: {
              total_data: res?.data?.data?.total_projects,
              data: res?.data?.data?.projects,
            },
          });
          dispatch({ type: 'SET_LOADING', payload: false });
          if (appFunctions?.offset !== res?.data?.data?.offset) {
            dispatch({
              type: 'SET_UPDATE_PAGE_SEARCH',
              payload: { my_projects: true },
            });
            dispatch({
              type: 'SET_APP_FUNCTIONS',
              payload: { offset: res?.data?.data?.offset },
            });
          } else {
            dispatch({
              type: 'SET_UPDATE_PAGE_SEARCH',
              payload: { my_projects: false },
            });
          }
        } else if (res?.status === 401) {
          setLogout(true);
        }
      })
      .catch((err) => {
        setShowError(true);
        makeToast('error', err?.response?.data?.message || 'Something went wrong!');
      });
  }, [appFunctions, dataUpdated]);

  const handleOffsetForProject = (value: number) => {
    dispatch({ type: 'SET_APP_FUNCTIONS', payload: { offset: value } });
  };

  const handleNewProjectClick = () => {
    dispatch({ type: 'SET_NEW_PROJECT_MODAL', payload: true });
    setWorkFlowData({
      ...workFlowData,
      new_project: true,
    });
    localStorage?.removeItem('triggerResponse');
    setDisableMode(false);
  };

  const searchData = (value: string) => {
    dispatch({ type: 'SET_APP_FUNCTIONS', payload: { search: value } });
  };

  usePageHeader({ className: 'relative' });

  return (
    <>
      {showError ? (
        <div className="flex w-full h-full items-center justify-center">
          <ErrorFallback />
        </div>
      ) : (
        <div>
          <div className="flex justify-between items-center pb-2 border-b-2">
            <h1 className="text-xl font-bold">{pageTitle}</h1>
            {permittedAccess?.[0]?.data?.includes('create') && (
              <button
                type="button"
                className="flex gap-x-1.5 items-center bg-[#122961] py-2 px-[18px] rounded-lg"
                onClick={handleNewProjectClick}
              >
                <img src={PlusIcon} alt="add" />
                <span className="text-white">{newProjectText}</span>
              </button>
            )}
          </div>

          <div>
            <div className="flex justify-between items-center mt-2">
              <h2 className="text-lg font-extrabold border-b-[3px] border-[#929598]">
                {existingProjectsText}
              </h2>
              <div className="w-[380px]">
                <SearchBar
                  className="py-[1px]"
                  handleSearchByChange={searchData}
                  value={appFunctions.search}
                  placeholder="Search by name and description"
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-y-4 gap-x-6 my-3">
            {appsData?.data?.length > 0 &&
              !loading &&
              appsData?.data?.map(
                (project: {
                  id: number;
                  name: string;
                  description: string;
                  username: string;
                  workflow_status: string;
                  workflow_trigger_type: string;
                }) => (
                  <ExistingProjectCard
                    key={Number(project)}
                    project={project}
                    setDataUpdated={(value) =>
                      dispatch({ type: 'SET_DATA_UPDATED', payload: value as boolean })
                    }
                    isEditAllowed={permittedAccess?.[0]?.data?.includes('update') ?? false}
                    isDeleteAllowed={permittedAccess?.[0]?.data?.includes('delete') ?? false}
                  />
                ),
              )}

            {appsData?.data?.length === 0 && !loading && (
              <h1 className="my-4 w-full text-center font-bold">{noProjectText}</h1>
            )}

            {loading && (
              <div className="w-full flex justify-center items-center my-4">
                <Loader width="8px" height="8px" />
              </div>
            )}
          </div>

          <Pagination
            activePage={1}
            rowsPerPage={9}
            setOffset={handleOffsetForProject}
            totalItems={appsData?.total_data}
            updatePageAsPerSearch={updatePageAsPerSearch.my_projects}
            setDataToDelete={() => {}}
          />

          <NewProjectModal
            open={newProjectModal}
            setNewProjectModal={(value) =>
              dispatch({ type: 'SET_NEW_PROJECT_MODAL', payload: value })
            }
            setDataUpdated={(value) =>
              dispatch({ type: 'SET_DATA_UPDATED', payload: value as boolean })
            }
          />
        </div>
      )}
      {logout && createPortal(<Logout />, document.body)}
    </>
  );
}

export default Automator;
